<script setup lang="ts">
/**
 * Каркас приватной части: сайдбар слева, шапка сверху, вью внутри.
 * Смонтирован как компонент маршрута `/`, поэтому переживает навигацию
 * между всеми дочерними экранами — сайдбар не перерисовывается.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ОРИЕНТИРЫ СТРАНИЦЫ
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Контент обёрнут в `<main id="main-content">`, а первым элементом стоит ссылка
 * «к содержимому». Это не формальность ради галочки в аудите: сайдбар содержит
 * до полутора десятков ссылок, и без пропуска клавиатурный пользователь
 * прощёлкивает их **на каждой** навигации, прежде чем добраться до таблицы.
 *
 * Ссылка видима только в фокусе (`sr-only focus:not-sr-only`) — она нужна тому,
 * кто ходит табом, и не должна занимать место у того, кто ходит мышью.
 */
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import AppHeader from './AppHeader.vue'
import AppSidebar from './AppSidebar.vue'
</script>

<template>
  <SidebarProvider>
    <!--
      Первый элемент в порядке обхода. `z-50`, потому что в фокусе он
      всплывает поверх липкой шапки, у которой `z-10`.
    -->
    <a
      href="#main-content"
      class="sr-only left-4 top-4 z-50 rounded-md bg-background px-4 py-2 text-sm font-medium shadow ring-2 ring-ring focus:not-sr-only focus:absolute"
    >
      Перейти к содержимому
    </a>

    <AppSidebar />

    <!--
      ⚠️ `min-w-0` — НЕ КОСМЕТИКА. Инсет это флекс-элемент строки «сайдбар +
      контент», а у флекс-элемента `min-width` по умолчанию `auto`, то есть он
      не сжимается уже своего min-content. Широкая таблица (участники с двумя
      десятками дополнительных граф) поднимает min-content всей колонки, инсет
      перестаёт умещаться рядом с сайдбаром — и горизонтальную полосу получает
      ВСЯ страница, хотя `overflow-x-auto` стоит на контейнере таблицы. С
      нулевым минимумом инсет сжимается до доступной ширины, и вбок едет только
      сама таблица.
    -->
    <SidebarInset class="min-w-0">
      <AppHeader />

      <!--
        `tabindex="-1"` — чтобы ссылка выше действительно переносила фокус:
        браузер ставит фокус на цель якоря, только если она фокусируема.
        Без этого перенесётся лишь прокрутка, а таб продолжится из сайдбара.
      -->
      <main id="main-content" tabindex="-1" class="flex flex-1 flex-col gap-6 p-4 outline-none md:p-6">
        <RouterView />
      </main>
    </SidebarInset>

    <!--
      Единственный экземпляр промис-диалога на всё приложение (см. `useConfirm.ts`).
      Живёт здесь, а не в `App.vue`: на `/login` подтверждать нечего. Без ровно
      одного смонтированного `<ConfirmDialog />` `await confirm(...)` вернёт
      промис, который никто никогда не разрешит, и обработчик удаления повиснет
      молча.
    -->
    <ConfirmDialog />
  </SidebarProvider>
</template>
